import { ProductType } from '../constants';
import type { ProductDetail } from '../model/product/detail';

export type CartPackOption = {
  sku: string;
  label: string;
  packCount: number | null;
  price: number;
  unitPrice: number | null;
  savingsPercent: number | null;
  isAvailable: boolean;
};

/** Quote one additional pack of the same size and color, independently of cart quantity. */
export function getCartPackUpgrade(product: ProductDetail, currentSku: string): CartPackOption | undefined {
  if (product.type !== ProductType.Variable) {
    return undefined;
  }
  const current = product.variations.find(({ sku }) => sku === currentSku);
  const options = getCartPackOptions(product, currentSku);
  const selected = options.find(({ sku }) => sku === currentSku);
  if (!current || selected?.packCount == null) {
    return undefined;
  }
  const currentPackCount = selected.packCount;
  return options
    .filter((option) => {
      const variation = product.variations.find(({ sku }) => sku === option.sku);
      return (
        option.packCount !== null &&
        option.packCount === currentPackCount + 1 &&
        option.isAvailable &&
        variation !== undefined &&
        variation.size === current.size &&
        variation.color === current.color &&
        option.unitPrice !== null
      );
    })
    .sort((a, b) => a.packCount! - b.packCount! || a.price - b.price)[0];
}

/** Catalog variations are selectable even when their attributes are not numeric packs. */
export function getCartPackOptions(product: ProductDetail, currentSku: string): CartPackOption[] {
  if (product.type !== ProductType.Variable) {
    return [];
  }
  if (!product.variations.some((variation) => variation.sku === currentSku)) {
    return [];
  }
  const sizesDiffer = new Set(product.variations.map((variation) => variation.size)).size > 1;
  const colorsDiffer = new Set(product.variations.map((variation) => variation.color)).size > 1;
  const packCountOf = (pack?: string): number | null => {
    const match = pack?.trim().match(/^(\d+)\s*packs?$/i);
    const count = match ? Number(match[1]) : 0;
    return Number.isSafeInteger(count) && count > 0 ? count : null;
  };
  return product.variations.flatMap((variation): CartPackOption[] => {
    const price = variation.salePrice ?? variation.originalPrice;
    if (!variation.sku || !Number.isFinite(price) || price < 0) {
      return [];
    }
    const packCount = packCountOf(variation.pack);
    const packUnit = packCount === 1 ? 'Pack' : 'Packs';
    const packLabel = packCount ? `${packCount} ${packUnit}` : variation.pack;
    const label =
      [sizesDiffer ? variation.size : null, colorsDiffer ? variation.color : null, packLabel]
        .filter(Boolean)
        .join(', ') ||
      variation.size ||
      variation.color ||
      variation.sku;
    const single = product.variations.find(
      (candidate) =>
        candidate.size === variation.size && candidate.color === variation.color && packCountOf(candidate.pack) === 1,
    );
    const singlePrice = single ? (single.salePrice ?? single.originalPrice) : null;
    const unitPrice = packCount ? price / packCount : null;
    const savings =
      singlePrice && singlePrice > 0 && unitPrice !== null ? Math.round((1 - unitPrice / singlePrice) * 100) : 0;
    return [
      {
        sku: variation.sku,
        label,
        packCount,
        price,
        unitPrice,
        savingsPercent: packCount !== null && packCount > 1 && savings > 0 ? savings : null,
        isAvailable: variation.status !== 'outofstock',
      },
    ];
  });
}
